// @ts-nocheck
/**
 * useDialerCallPoolPorts — Ports XState pour le pool d'appels (audio + Twilio).
 *
 * 2026-06-04 (Fowler etape 7/x du decoupage) — Extrait du god-context
 * `DialerContext.tsx`. Les ports sont les bridges qui permettent au
 * `useShadowCallPool` de declencher des side-effects audio (beeps),
 * Twilio (hangup), et React Query (invalidate + fetch).
 *
 * Responsabilite unique :
 *   "Fournir une instance memoizee des callbacks que les machines XState
 *    du pool peuvent appeler sans se preoccuper du cablage React."
 *
 * Side-effects audio :
 *   - playDialBeep : son court 320Hz triangle, throttle 1500ms
 *   - playConnectionBeep : ding 880Hz sinus doux, throttle 1500ms
 *   Tous deux gates par soundEnabled, freezed si connectedContact.
 *
 * Side-effects backend :
 *   - hangupTwilio(callId) : appelle TwilioCalls.hangupCall si external_call_id
 *   - fetchEnrichedContact(callId) : recharge le ParallelCall + dispatch
 *     ENRICH_CONTACT au callPool actor
 *   - saveQualification(callId, outcome, notes) : update ParallelCall
 *   - hangupVoicemail(callId) : hangup Twilio + marquage call_outcome='voicemail'
 *     (skip si voicemailMode != 'off')
 */

import { useMemo, useRef } from 'react';
// @ts-ignore — api-client est en .js
import { api } from '@/api/api-client';
// @ts-ignore — react-query en .js sans types stricts
import type { QueryClient } from '@tanstack/react-query';
import * as TwilioCalls from '@/services/dialer/TwilioCallService';
import type { ParallelCall } from '@/contexts/DialerDataContext';

interface UseDialerCallPoolPortsArgs {
  queryClient: QueryClient;
  activeCalls: ParallelCall[];
  soundEnabled: boolean;
  voicemailMode: string;
  /** Ref vers le connectedContact pour freezer les beeps pendant un appel. */
  connectedContactRef: React.MutableRefObject<{ callId?: string; call_id?: string } | null>;
}

export interface DialerCallPoolPorts {
  queryClient: QueryClient;
  playDialBeep: () => void;
  playConnectionBeep: () => void;
  hangupTwilio: (callId: string) => Promise<void>;
  fetchEnrichedContact: (callId: string) => Promise<void>;
  saveQualification: (callId: string, outcome: string, notes?: string) => Promise<void>;
  hangupVoicemail: (callId: string) => Promise<void>;
}

export function useDialerCallPoolPorts({
  queryClient,
  activeCalls,
  soundEnabled,
  voicemailMode,
  connectedContactRef,
}: UseDialerCallPoolPortsArgs): DialerCallPoolPorts {
  const lastDialBeepAtRef = useRef(0);
  const lastConnectionBeepAtRef = useRef(0);

  return useMemo<DialerCallPoolPorts>(
    () => ({
      queryClient,
      playDialBeep: () => {
        if (!soundEnabled) return;
        if (connectedContactRef.current) return;
        const now = Date.now();
        if (now - lastDialBeepAtRef.current < 1500) return;
        lastDialBeepAtRef.current = now;
        try {
          const Ctx = (window as unknown as {
            AudioContext: typeof AudioContext;
            webkitAudioContext?: typeof AudioContext;
          }).AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
          if (!Ctx) return;
          const ctx = new Ctx();
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = 'triangle';
          osc.frequency.value = 320;
          const t0 = ctx.currentTime;
          gain.gain.setValueAtTime(0.0001, t0);
          gain.gain.exponentialRampToValueAtTime(0.04, t0 + 0.02);
          gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.20);
          osc.connect(gain).connect(ctx.destination);
          osc.start(t0);
          osc.stop(t0 + 0.22);
          setTimeout(() => ctx.close().catch(() => undefined), 300);
        } catch (_e) {
          /* swallow audio errors */
        }
      },
      playConnectionBeep: () => {
        if (!soundEnabled) return;
        const now = Date.now();
        if (now - lastConnectionBeepAtRef.current < 1500) return;
        lastConnectionBeepAtRef.current = now;
        try {
          const Ctx = (window as unknown as {
            AudioContext: typeof AudioContext;
            webkitAudioContext?: typeof AudioContext;
          }).AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
          if (!Ctx) return;
          const ctx = new Ctx();
          const o = ctx.createOscillator();
          const g = ctx.createGain();
          o.type = 'sine';
          o.frequency.value = 880;
          const t0 = ctx.currentTime;
          g.gain.setValueAtTime(0.0001, t0);
          g.gain.exponentialRampToValueAtTime(0.03, t0 + 0.01);
          g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.20);
          o.connect(g).connect(ctx.destination);
          o.start(t0);
          o.stop(t0 + 0.22);
          setTimeout(() => ctx.close().catch(() => undefined), 350);
        } catch (_e) {
          /* swallow */
        }
      },
      hangupTwilio: async (callId: string) => {
        try {
          const c = activeCalls.find((ac) => ac.id === callId);
          if (c?.external_call_id) {
            await TwilioCalls.hangupCall(c.external_call_id);
          }
        } catch (e) {
          console.warn('[callPool] hangupTwilio', (e as Error)?.message);
        }
      },
      fetchEnrichedContact: async (callId: string) => {
        try {
          const call = await api.entities.ParallelCall.get(callId);
          if (!call) return;
          const enriched = {
            callId,
            prospect_id: call.prospect_id,
            prospect_name: call.prospect_name,
            prospect_phone: call.prospect_phone,
            prospect_email: call.prospect_email || '',
            prospect_company: call.prospect_company,
            prospect_title: call.prospect_title || '',
            linkedin_url: call.linkedin_url || '',
            website: call.website || '',
            notes: call.notes || '',
          };
          const actor = (window as unknown as { __callPool?: { get: (id: string) => { send: (msg: unknown) => void } | undefined } }).__callPool?.get(callId);
          if (actor) actor.send({ type: 'ENRICH_CONTACT', enrichedContact: enriched });
        } catch (e) {
          console.warn('[callPool] fetchEnrichedContact', (e as Error)?.message);
        }
      },
      saveQualification: async (callId: string, outcome: string, notes?: string) => {
        try {
          await api.entities.ParallelCall.update(callId, {
            call_outcome: outcome,
            call_notes: notes || '',
            ended_at: new Date().toISOString(),
          });
        } catch (e) {
          console.warn('[callPool] saveQualification', (e as Error)?.message);
          throw e;
        }
      },
      hangupVoicemail: async (callId: string) => {
        if (voicemailMode !== 'off') return;
        const c = activeCalls.find((ac) => ac.id === callId);
        console.log('[callPool] hangupVoicemail callId=', callId, 'phone=', c?.prospect_phone);
        try {
          if (c?.external_call_id) await TwilioCalls.hangupCall(c.external_call_id);
        } catch (e) {
          console.warn('[callPool] hangupVoicemail twilio failed', (e as Error)?.message);
        }
        try {
          await api.entities.ParallelCall.update(callId, {
            call_status: 'completed',
            call_outcome: 'voicemail',
            ended_at: new Date().toISOString(),
          });
        } catch (e) {
          console.warn(
            '[callPool] hangupVoicemail update failed',
            (e as Error)?.message,
          );
        }
      },
    }),
    [queryClient, activeCalls, soundEnabled, voicemailMode, connectedContactRef],
  );
}
